import { addDatadogRumAction } from 'vendor/datadog-rum';
/**
 * The page the write originated on. The task pane is a drawer over another
 * route, so the pathname still reflects the host page; callers that KNOW they
 * are the pane (no host route context) can pass an explicit override.
 */ export const currentTaskSurface = ()=>{
    if (typeof window === 'undefined') {
        return 'other';
    }
    const path = window.location.pathname;
    if (path.includes('/plan')) {
        return 'plan';
    }
    if (path.includes('/tasks/list') || path.endsWith('/tasks')) {
        return 'list';
    }
    if (path.includes('/tasks/board')) {
        return 'board';
    }
    return 'other';
};
const emit = (event, context)=>{
    addDatadogRumAction(event, {
        surface: currentTaskSurface(),
        ...context
    });
};
export const taskFunnel = {
    statusChange: (ctx)=>emit('task.status.change', ctx),
    /**
   * `status`/`message` carry the server rejection (HTTP status + reason) so the
   * error is self-explanatory in RUM without correlating to the failed
   * request. A 422 here is a backend validation/business-rule rejection, not a
   * client bug — the optimistic write already rolled back.
   */ statusError: (ctx)=>emit('task.status.error', ctx),
    subtaskAdd: (ctx)=>emit('task.subtask.add', ctx),
    subtaskAddError: (ctx)=>emit('task.subtask.add_error', ctx),
    /**
   * Any optimistic field op-update from the task drawer (default fields,
   * custom fields, effort, epic, dates, assignees, privacy, …). `field`
   * names the edited attribute(s) so drops/errors can be sliced per field
   * type. `kind` distinguishes the specific write for the ones that aren't a
   * plain field patch (privacy toggle, date edit with dependency cascade).
   */ fieldUpdate: (ctx)=>emit('task.field_update', ctx),
    fieldUpdateError: (ctx)=>emit('task.field_update.error', ctx)
};
/**
 * Task-dependency funnel — the task-pane "blocking tasks" / predecessor flows.
 *
 * Covers BOTH add UIs (legacy inline picker + the new predecessor modal) and
 * every branch of the flow, so drop-offs and errors are sliceable in Datadog:
 *
 *   add.start           — a dependency candidate was selected
 *   add.conflict        — DEVIATION: the add would shift other entities' dates,
 *                         so the conflict modal opens instead of adding
 *   add.missing_source  — DEVIATION: the candidate isn't in the loaded plan
 *                         task map, so cross-entity shifts can't be computed
 *                         and it's added directly (this branch previously threw
 *                         "Cannot read properties of undefined (reading 'id')"
 *                         and silently dropped the add — see TC-104)
 *   add.direct          — no conflict; added directly
 *   added / add.error   — the persisted add resolved / rejected
 *   conflict_confirm|cancel — the conflict modal was confirmed / dismissed
 *   removed / remove.error  — dependency removal resolved / rejected
 *   predecessor.*       — the modal flow's start / added(kind) / error(kind)
 *
 * `verified`-style health ratio: `added` vs `add.error`, and `add.conflict`
 * share (a rising conflict share = date model drifting). Fire-and-forget,
 * RUM-init-safe; carries only ids + low-cardinality enums.
 */ export const dependencyFunnel = {
    addStart: (ctx)=>emit('task.dependency.add.start', ctx),
    addConflict: (ctx)=>emit('task.dependency.add.conflict', ctx),
    addMissingSource: (ctx)=>emit('task.dependency.add.missing_source', ctx),
    addDirect: (ctx)=>emit('task.dependency.add.direct', ctx),
    added: (ctx)=>emit('task.dependency.added', ctx),
    addError: (ctx)=>emit('task.dependency.add.error', ctx),
    conflictConfirm: (ctx)=>emit('task.dependency.conflict_confirm', ctx),
    conflictCancel: (ctx)=>emit('task.dependency.conflict_cancel', ctx),
    removed: (ctx)=>emit('task.dependency.removed', ctx),
    removeError: (ctx)=>emit('task.dependency.remove.error', ctx),
    predecessorStart: (ctx)=>emit('task.dependency.predecessor.start', ctx),
    predecessorAdded: (ctx)=>emit('task.dependency.predecessor.added', ctx),
    predecessorError: (ctx)=>emit('task.dependency.predecessor.error', ctx)
};
